import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FiAlertTriangle, FiHelpCircle } from "react-icons/fi";
import { shakeModal } from "../utils/modalAttention";
import { formatujDdMmRrrr, parsujDateZKlawiatury } from "../components/common/DatePicker/NaglowekKalendarza";
import { KOMUNIKAT_ROK, rokWZakresie, rokZISO } from "../components/common/DatePicker/GraniceDaty";
import s from "./ConfirmDialog.module.css";

/**
 * Globalny, ładny dialog potwierdzenia — zamiennik natywnego window.confirm().
 * Użycie:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ message: "Na pewno usunąć?", danger: true, confirmText: "Usuń" }))) return;
 *
 * Zwraca Promise<boolean>. Mbuilt na portalu, więc działa z dowolnego komponentu funkcyjnego.
 *
 * [poprawka NOWE-ZGLOSZENIE 2026-09-28] KLAWIATURA JAK W DIALOGU WAI-ARIA (alertdialog). Pytanie stało dotąd POZA
 * stosem okien `Modal`, więc pod spodem działała dalej pułapka Tab okna gospodarza (Tab uciekał pod nakładkę),
 * a Escape trafiał do okna pod spodem i nic nie zamykał — z klawiatury nie dało się wybrać „Wróć do wniosku”.
 * Teraz, dopóki pytanie jest otwarte: Escape = odpowiedź „nie” (przycisk anulowania), Tab/Shift+Tab krąży po
 * przyciskach pytania, a oba klawisze są zatrzymywane w fazie przechwytywania okna — Modal ani szuflada pod spodem
 * ich nie dostają. Po odpowiedzi fokus wraca tam, skąd pytanie przyszło. Opcja `fokusNaAnulowaniu` stawia fokus
 * startowy na przycisku anulowania (pytanie o porzucenie danych: odruchowy Enter nie może ich skasować).
 *
 * [ui1006, projekt czterech stron §2.4 i §2.7] PYTANIE O CZYNNOŚĆ GROŹNĄ — wszystkie stopnie przez TO SAMO okno.
 * Nowe opcje są DOKŁADANE; pytanie bez żadnej z nich ma DOM i zachowanie co do znaku jak dotąd (dziesiątki wywołań
 * w aplikacji — pilnuje `context/__tests__/pytanieOCzynnoscGrozna.ui1006.test.js`):
 *   • `skutki: string[]`      — lista „Co się stanie” (zdania o skutku, z liczbami z serwera, gdy są);
 *   • `jakCofnac: string`     — ostatnie zdanie „Jak to cofnąć: …” (stopień 1a);
 *   • `nieCofniesz: string`   — ostatnie zdanie o tym, czego nie da się cofnąć (stopnie 1b, 2, 3);
 *   • `wymagaj: { rozumiem }` — pole wyboru z tym zdaniem; bez zaznaczenia przycisk potwierdzenia jest nieczynny,
 *     a POD nim stoi zdanie, co zrobić (stopień 3);
 *   • `wymagaj: { wpisz, etykieta }` — pole, w które trzeba wpisać liczbę albo nazwę (stopień 3 z wpisaniem);
 *   • `pola: [{ nazwa, etykieta, rodzaj: "tekst"|"data"|"wybor"|"pole-wyboru", opcje, wymagane, domyslna }]` (najwyżej 3)
 *     — powód, komentarz, data albo spółka wpisywane W pytaniu. Wtedy `confirm()` oddaje `false` albo OBIEKT wartości
 *     `{ [nazwa]: wartość }` (data jako "RRRR-MM-DD"); bez `pola` — jak dotąd `true` / `false`.
 * Fokus startowy: jawne `fokusNaAnulowaniu` wygrywa; bez niego pytanie z `danger` I którąkolwiek nową opcją (czyli
 * stopień 2–3) stawia fokus na „Anuluj”; pytanie z polami i bez `danger` — na pierwszym polu; reszta jak dotąd.
 * Data jest wpisywana z klawiatury (dd.mm.rrrr, ten sam czytnik co w kalendarzu aplikacji): kalendarz wysuwany stoi
 * na warstwie niższej niż pytanie i otwierałby się pod nim. Granice lat są te same co w kalendarzu (1910–2099,
 * `GraniceDaty`): data istniejąca, ale spoza granic (np. rok 3423 z ośmiu wpisanych cyfr), BLOKUJE potwierdzenie
 * zdaniem o dozwolonych latach — nie zapisuje się po cichu.
 * Długie pytanie (lista skutków + pola + „Rozumiem”) nie wychodzi poza niskie okno przeglądarki: przewija się
 * jego treść, przyciski zostają na ekranie (arkusz, `.dialogRozszerzony`).
 *
 * [mojdzial1006, paczka MD-KIT, D-10, decyzja D7] DWIE OPCJE MOJEGO DZIAŁU (dokładane — bez nich jak dotąd):
 *   • `naglowekOsoby: { awatar, nazwa, opis }` — wyróżniona linia osoby NAD „Co się stanie” (zdjęcie, nazwisko, stanowisko,
 *     dział): ochrona przed pomyłką osoby w serii decyzji („Zatwierdź wniosek…” Anny, a nie Jana);
 *   • rodzaj pola `haslo` — `<input type="password" autocomplete="current-password">` NIEKONTROLOWANY: wartość żyje tylko
 *     w polu i w pamięci do chwili oddania (`confirm()` zwraca ją w obiekcie wartości), nigdy w stanie okna, w `textContent`,
 *     w adresie, w `oglos()` ani w konsoli; po zamknięciu pytania pole znika razem z wartością;
 *   • rodzaj pola `kod` — `<input inputMode="numeric" autocomplete="one-time-code" maxLength=6 pattern="\d{6}">`: telefon
 *     podpowiada kod z SMS, pole przyjmuje WYŁĄCZNIE cyfry (najwyżej 6), a pole wymagane blokuje potwierdzenie do 6 cyfr.
 */
const ConfirmContext = createContext(null);

/** Co wolno dosięgnąć klawiszem Tab w pytaniu: przyciski jak dotąd, a w pytaniu z polami także pola. */
const DO_FOKUSU = "button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])";
const LIMIT_POL = 3;
const RODZAJE_POL = ["tekst", "data", "wybor", "pole-wyboru", "haslo", "kod"];
const DLUGOSC_KODU = 6;

const niepuste = (v) => typeof v === "string" && v.trim() !== "";
const listaZdan = (v) => (Array.isArray(v) ? v.filter(niepuste) : []);

/** Opcje pytania → to, co okno rysuje ponad dotychczasowy kształt (`null` = pytanie zwykłe, DOM jak dotąd). */
const czytajRozszerzenie = (options) => {
    const skutki = listaZdan(options.skutki);
    const jakCofnac = niepuste(options.jakCofnac) ? options.jakCofnac.trim() : "";
    const nieCofniesz = niepuste(options.nieCofniesz) ? options.nieCofniesz.trim() : "";
    const w = options.wymagaj && typeof options.wymagaj === "object" ? options.wymagaj : null;
    const wymagaj = w && niepuste(w.rozumiem)
        ? { rodzaj: "rozumiem", zdanie: w.rozumiem.trim() }
        : (w && (niepuste(w.wpisz) || typeof w.wpisz === "number")
            ? { rodzaj: "wpisz", wzor: String(w.wpisz).trim(), etykieta: niepuste(w.etykieta) ? w.etykieta.trim() : `Wpisz ${String(w.wpisz).trim()}, żeby potwierdzić` }
            : null);
    const surowePola = Array.isArray(options.pola) ? options.pola.filter((p) => p && niepuste(p.nazwa) && niepuste(p.etykieta)) : [];
    if (process.env.NODE_ENV !== "production") {
        if (surowePola.length > LIMIT_POL) throw new Error(`Pytanie ma najwyżej ${LIMIT_POL} pola — dłuższy formularz to szuflada albo kreator.`);
        const obcy = surowePola.find((p) => !RODZAJE_POL.includes(p.rodzaj || "tekst"));
        if (obcy) throw new Error(`Pytanie nie zna rodzaju pola „${obcy.rodzaj}”.`);
    }
    const pola = surowePola.slice(0, LIMIT_POL).map((p) => ({
        nazwa: p.nazwa,
        etykieta: p.etykieta,
        rodzaj: RODZAJE_POL.includes(p.rodzaj) ? p.rodzaj : "tekst",
        opcje: Array.isArray(p.opcje) ? p.opcje : [],
        wymagane: !!p.wymagane,
        domyslna: p.domyslna,
    }));
    const o = options.naglowekOsoby && typeof options.naglowekOsoby === "object" ? options.naglowekOsoby : null;
    const naglowekOsoby = o && niepuste(o.nazwa) ? { awatar: o.awatar || null, nazwa: o.nazwa.trim(), opis: niepuste(o.opis) ? o.opis.trim() : "" } : null;
    if (skutki.length === 0 && !jakCofnac && !nieCofniesz && !wymagaj && pola.length === 0 && !naglowekOsoby) return null;
    return { skutki, jakCofnac, nieCofniesz, wymagaj, pola, naglowekOsoby };
};

const wartoscStartowa = (pole) => {
    // Hasło NIE ma wartości w stanie okna — w stanie stoi tylko, czy pole jest wypełnione (D-10).
    if (pole.rodzaj === "haslo") return false;
    if (pole.rodzaj === "kod") return "";
    if (pole.rodzaj === "pole-wyboru") return pole.domyslna === true;
    if (pole.rodzaj === "data") return formatujDdMmRrrr(typeof pole.domyslna === "string" ? pole.domyslna : "") || "";
    return pole.domyslna === undefined || pole.domyslna === null ? "" : String(pole.domyslna);
};

/** Pierwszy powód, dla którego nie wolno jeszcze potwierdzić — zdaniem, które stanie POD przyciskiem. */
const powodBlokady = (roz, wartosci, rozumiem, wpisane) => {
    for (const pole of roz.pola) {
        const v = wartosci[pole.nazwa];
        if (pole.rodzaj === "data") {
            const iso = niepuste(v) ? parsujDateZKlawiatury(v) : null;
            if (niepuste(v) && !iso) return `Wpisz datę jako dd.mm.rrrr w polu „${pole.etykieta}”.`;
            // Data ISTNIEJE w kalendarzu, ale leży poza granicami lat aplikacji — to nie literówka zapisu, tylko zły rok.
            if (iso && !rokWZakresie(rokZISO(iso))) return `${KOMUNIKAT_ROK} Popraw pole „${pole.etykieta}”.`;
            if (pole.wymagane && !niepuste(v)) return `Wypełnij pole „${pole.etykieta}”.`;
        } else if (pole.rodzaj === "pole-wyboru") {
            if (pole.wymagane && v !== true) return `Zaznacz pole „${pole.etykieta}”.`;
        } else if (pole.rodzaj === "haslo") {
            if (pole.wymagane && v !== true) return `Wpisz hasło w polu „${pole.etykieta}”.`;
        } else if (pole.rodzaj === "kod") {
            if ((pole.wymagane || niepuste(v)) && !new RegExp(`^\\d{${DLUGOSC_KODU}}$`).test(String(v || ""))) return `Wpisz ${DLUGOSC_KODU} cyfr kodu w polu „${pole.etykieta}”.`;
        } else if (pole.wymagane && !niepuste(v)) {
            return pole.rodzaj === "wybor" ? `Wybierz wartość w polu „${pole.etykieta}”.` : `Wypełnij pole „${pole.etykieta}”.`;
        }
    }
    if (roz.wymagaj && roz.wymagaj.rodzaj === "rozumiem" && !rozumiem) return "Zaznacz pole powyżej.";
    if (roz.wymagaj && roz.wymagaj.rodzaj === "wpisz"
        && wpisane.trim().toLocaleLowerCase("pl") !== roz.wymagaj.wzor.toLocaleLowerCase("pl")) {
        return `Wpisz „${roz.wymagaj.wzor}” w polu powyżej.`;
    }
    return "";
};

const wartosciDoOddania = (roz, wartosci, sekrety = {}) => {
    const wynik = {};
    roz.pola.forEach((pole) => {
        const v = wartosci[pole.nazwa];
        if (pole.rodzaj === "haslo") wynik[pole.nazwa] = sekrety[pole.nazwa] && typeof sekrety[pole.nazwa].value === "string" ? sekrety[pole.nazwa].value : "";
        else if (pole.rodzaj === "kod") wynik[pole.nazwa] = String(v || "").replace(/\D/g, "").slice(0, DLUGOSC_KODU);
        else if (pole.rodzaj === "pole-wyboru") wynik[pole.nazwa] = v === true;
        else if (pole.rodzaj === "data") wynik[pole.nazwa] = niepuste(v) ? parsujDateZKlawiatury(v) || "" : "";
        else wynik[pole.nazwa] = typeof v === "string" ? v.trim() : "";
    });
    return wynik;
};

/**
 * Część pytania ponad dotychczasowy kształt: „Co się stanie”, pola, warunek potwierdzenia. Rysowana WYŁĄCZNIE dla
 * pytania z nowymi opcjami — zwykłe pytanie nie dostaje ani jednego węzła więcej.
 */
const TrescRozszerzona = ({ roz, wartosci, ustawWartosc, rozumiem, setRozumiem, wpisane, setWpisane, fokusNaPolu, sekrety }) => {
    let pierwszePole = fokusNaPolu;
    const czyPierwsze = () => { const tak = pierwszePole; pierwszePole = false; return tak; };
    return (
        <div className={s.rozszerzenie}>
            {roz.naglowekOsoby && (
                <div className={s.naglowekOsoby} data-naglowek-osoby="">
                    {roz.naglowekOsoby.awatar ? <span className={s.awatarOsoby} aria-hidden="true">{roz.naglowekOsoby.awatar}</span> : null}
                    <span className={s.daneOsoby}>
                        <strong className={s.nazwaOsoby}>{roz.naglowekOsoby.nazwa}</strong>
                        {roz.naglowekOsoby.opis ? <span className={s.opisOsoby}>{roz.naglowekOsoby.opis}</span> : null}
                    </span>
                </div>
            )}
            {roz.skutki.length > 0 && (
                <div className={s.skutki}>
                    <p className={s.skutkiTytul}>Co się stanie</p>
                    <ul className={s.skutkiLista}>
                        {roz.skutki.map((zdanie, i) => <li key={i}>{zdanie}</li>)}
                    </ul>
                </div>
            )}
            {roz.jakCofnac && <p className={s.zdanieKoncowe} data-rodzaj="jak-cofnac"><strong>Jak to cofnąć:</strong> {roz.jakCofnac}</p>}
            {roz.nieCofniesz && <p className={s.zdanieKoncowe} data-rodzaj="nie-cofniesz">{roz.nieCofniesz}</p>}
            {roz.pola.map((pole) => {
                const id = `pytanie-pole-${pole.nazwa}`;
                const v = wartosci[pole.nazwa];
                if (pole.rodzaj === "pole-wyboru") {
                    return (
                        <label key={pole.nazwa} className={s.poleWyboru} htmlFor={id}>
                            <input id={id} type="checkbox" checked={v === true} autoFocus={czyPierwsze()} onChange={(e) => ustawWartosc(pole.nazwa, e.target.checked)} />
                            <span>{pole.etykieta}</span>
                        </label>
                    );
                }
                return (
                    <div key={pole.nazwa} className={s.pole}>
                        <label className={s.etykietaPola} htmlFor={id}>
                            {pole.etykieta}{pole.wymagane ? <span aria-hidden="true"> *</span> : null}
                        </label>
                        {pole.rodzaj === "wybor" ? (
                            <select id={id} className={s.kontrolka} value={v} autoFocus={czyPierwsze()} aria-required={pole.wymagane || undefined} onChange={(e) => ustawWartosc(pole.nazwa, e.target.value)}>
                                <option value="">Wybierz…</option>
                                {pole.opcje.map((o) => {
                                    const wartosc = o && typeof o === "object" ? o.wartosc : o;
                                    const etykieta = o && typeof o === "object" ? (o.etykieta ?? o.wartosc) : o;
                                    return <option key={String(wartosc)} value={String(wartosc)}>{etykieta}</option>;
                                })}
                            </select>
                        ) : pole.rodzaj === "haslo" ? (
                            /* D-10: hasło NIEKONTROLOWANE — wartość tylko w polu (ref), w stanie okna sam znak „wypełnione”. */
                            <input
                                id={id}
                                type="password"
                                className={s.kontrolka}
                                autoComplete="current-password"
                                autoFocus={czyPierwsze()}
                                aria-required={pole.wymagane || undefined}
                                ref={(el) => { if (sekrety) sekrety.current[pole.nazwa] = el; }}
                                onChange={(e) => ustawWartosc(pole.nazwa, e.target.value !== "")}
                                data-pole-sekretu=""
                            />
                        ) : pole.rodzaj === "kod" ? (
                            <input
                                id={id}
                                type="text"
                                inputMode="numeric"
                                autoComplete="one-time-code"
                                maxLength={DLUGOSC_KODU}
                                pattern={`\\d{${DLUGOSC_KODU}}`}
                                className={s.kontrolka}
                                value={v}
                                autoFocus={czyPierwsze()}
                                aria-required={pole.wymagane || undefined}
                                onChange={(e) => ustawWartosc(pole.nazwa, e.target.value.replace(/\D/g, "").slice(0, DLUGOSC_KODU))}
                            />
                        ) : pole.rodzaj === "data" ? (
                            <input id={id} type="text" inputMode="numeric" className={s.kontrolka} value={v} placeholder="dd.mm.rrrr" autoComplete="off" autoFocus={czyPierwsze()} aria-required={pole.wymagane || undefined} onChange={(e) => ustawWartosc(pole.nazwa, e.target.value)} />
                        ) : (
                            <textarea id={id} className={s.kontrolka} rows={2} value={v} autoFocus={czyPierwsze()} aria-required={pole.wymagane || undefined} onChange={(e) => ustawWartosc(pole.nazwa, e.target.value)} />
                        )}
                    </div>
                );
            })}
            {roz.wymagaj && roz.wymagaj.rodzaj === "rozumiem" && (
                <label className={s.poleWyboru} htmlFor="pytanie-rozumiem">
                    <input id="pytanie-rozumiem" type="checkbox" checked={rozumiem} autoFocus={czyPierwsze()} onChange={(e) => setRozumiem(e.target.checked)} />
                    <span>{roz.wymagaj.zdanie}</span>
                </label>
            )}
            {roz.wymagaj && roz.wymagaj.rodzaj === "wpisz" && (
                <div className={s.pole}>
                    <label className={s.etykietaPola} htmlFor="pytanie-wpisz">{roz.wymagaj.etykieta}</label>
                    <input id="pytanie-wpisz" type="text" className={s.kontrolka} value={wpisane} autoComplete="off" autoFocus={czyPierwsze()} onChange={(e) => setWpisane(e.target.value)} />
                </div>
            )}
        </div>
    );
};

export const ConfirmDialogProvider = ({ children }) => {
    const [state, setState] = useState(null);
    const resolverRef = useRef(null);
    const dialogRef = useRef(null);
    const poprzedniFokusRef = useRef(null);
    /* Stan pól pytania rozszerzonego. Zwykłe pytanie go nie dotyka (zostaje pusty). */
    const [wartosci, setWartosci] = useState({});
    const [rozumiem, setRozumiem] = useState(false);
    const [wpisane, setWpisane] = useState("");
    /* D-10: pola `haslo` — elementy pól (wartość czytana dopiero przy potwierdzeniu, nigdy w stanie). */
    const sekrety = useRef({});

    const confirm = useCallback((opts = {}) => {
        const options = typeof opts === "string" ? { message: opts } : opts;
        // Element z fokusem SPRZED pytania — tam wraca fokus po odpowiedzi (drugie pytanie nie nadpisuje pierwszego źródła).
        if (!resolverRef.current && typeof document !== "undefined") poprzedniFokusRef.current = document.activeElement;
        const rozszerzenie = czytajRozszerzenie(options);
        const danger = options.danger ?? false;
        return new Promise((resolve) => {
            resolverRef.current = resolve;
            setWartosci(rozszerzenie ? Object.fromEntries(rozszerzenie.pola.map((p) => [p.nazwa, wartoscStartowa(p)])) : {});
            setRozumiem(false);
            setWpisane("");
            setState({
                title: options.title || "Potwierdzenie",
                /* Pytanie rozszerzone bez własnego zdania nie dostaje ogólnego „Czy na pewno…” — mówi za nie lista skutków. */
                message: options.message || (rozszerzenie ? null : "Czy na pewno chcesz kontynuować?"),
                confirmText: options.confirmText || "Potwierdź",
                cancelText: options.cancelText || "Anuluj",
                danger,
                /* Jawna opcja wygrywa (także `false`); bez niej stopień 2–3 — `danger` z nową opcją — startuje na „Anuluj”. */
                fokusNaAnulowaniu: options.fokusNaAnulowaniu !== undefined && options.fokusNaAnulowaniu !== null
                    ? !!options.fokusNaAnulowaniu
                    : !!(rozszerzenie && danger),
                rozszerzenie,
            });
        });
    }, []);

    const close = useCallback((result) => {
        sekrety.current = {};
        setState(null);
        const poprzedni = poprzedniFokusRef.current;
        poprzedniFokusRef.current = null;
        if (poprzedni && typeof poprzedni.focus === "function" && typeof document !== "undefined" && document.contains(poprzedni)) {
            try { poprzedni.focus(); } catch { /* element właśnie znika — fokus zostaje tam, gdzie jest */ }
        }
        if (resolverRef.current) {
            resolverRef.current(result);
            resolverRef.current = null;
        }
    }, []);

    /* Klawiatura pytania: Escape = „nie”, Tab krąży po jego przyciskach. Nasłuch w fazie PRZECHWYTYWANIA na `window`
       i `stopPropagation` — okno (Modal) i szuflada pod spodem słuchają na `document`, więc tych klawiszy nie dostaną. */
    useEffect(() => {
        if (!state) return undefined;
        const naKlawisz = (e) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                close(false);
                return;
            }
            if (e.key !== "Tab") return;
            const dialog = dialogRef.current;
            if (!dialog) return;
            e.preventDefault();
            e.stopPropagation();
            const lista = Array.from(dialog.querySelectorAll(DO_FOKUSU));
            if (lista.length === 0) return;
            const i = lista.indexOf(document.activeElement);
            const j = e.shiftKey
                ? (i <= 0 ? lista.length - 1 : i - 1)
                : (i < 0 || i === lista.length - 1 ? 0 : i + 1);
            lista[j].focus();
        };
        window.addEventListener("keydown", naKlawisz, true);
        return () => window.removeEventListener("keydown", naKlawisz, true);
    }, [state, close]);

    /* Pytanie rozszerzone: czego jeszcze brakuje do potwierdzenia, co oddać i gdzie postawić fokus. Dla zwykłego
       pytania wszystkie trzy wartości są neutralne — przyciski dostają dokładnie te atrybuty, co dotąd. */
    const roz = state ? state.rozszerzenie : null;
    const blokada = roz ? powodBlokady(roz, wartosci, rozumiem, wpisane) : "";
    const maPola = !!roz && (roz.pola.length > 0 || !!roz.wymagaj);
    const fokusNaPolu = !!state && maPola && !state.fokusNaAnulowaniu;
    const potwierdz = () => {
        if (blokada) return;
        close(roz && roz.pola.length > 0 ? wartosciDoOddania(roz, wartosci, sekrety.current) : true);
    };
    const ustawWartosc = (nazwa, wartosc) => setWartosci((poprzednie) => ({ ...poprzednie, [nazwa]: wartosc }));

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && createPortal(
                <div className={s.overlay} onClick={shakeModal}>
                    <div
                        ref={dialogRef}
                        className={roz ? `${s.dialog} ${s.dialogRozszerzony}` : s.dialog}
                        onClick={(e) => e.stopPropagation()}
                        role="alertdialog"
                        aria-modal="true"
                        aria-label={state.title}
                    >
                        <div className={`${s.iconWrap} ${state.danger ? s.iconDanger : s.iconInfo}`}>
                            {state.danger ? <FiAlertTriangle size={22} /> : <FiHelpCircle size={22} />}
                        </div>
                        <h3 className={s.title}>{state.title}</h3>
                        {state.message ? <p className={s.message}>{state.message}</p> : null}
                        {roz && (
                            <TrescRozszerzona
                                roz={roz}
                                wartosci={wartosci}
                                ustawWartosc={ustawWartosc}
                                rozumiem={rozumiem}
                                setRozumiem={setRozumiem}
                                wpisane={wpisane}
                                setWpisane={setWpisane}
                                fokusNaPolu={fokusNaPolu}
                                sekrety={sekrety}
                            />
                        )}
                        <div className={s.actions}>
                            <button
                                type="button"
                                className={s.cancelBtn}
                                onClick={() => close(false)}
                                autoFocus={state.fokusNaAnulowaniu}
                            >
                                {state.cancelText}
                            </button>
                            <button
                                type="button"
                                className={`${s.confirmBtn} ${state.danger ? s.confirmDanger : ""}`}
                                onClick={potwierdz}
                                autoFocus={!state.fokusNaAnulowaniu && !fokusNaPolu}
                                disabled={blokada ? true : undefined}
                                aria-describedby={blokada ? "pytanie-powod-blokady" : undefined}
                            >
                                {state.confirmText}
                            </button>
                        </div>
                        {blokada ? <p id="pytanie-powod-blokady" className={s.powodBlokady}>{blokada}</p> : null}
                    </div>
                </div>,
                document.body
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const ctx = useContext(ConfirmContext);
    if (!ctx) throw new Error("useConfirm must be used within ConfirmDialogProvider");
    return ctx;
};

/**
 * 🔴 WARIANT BEZ WYJĄTKU — zwraca `null`, gdy providera nad komponentem NIE MA.
 *
 * Po co: `useConfirm` RZUCA, a to znaczy BIAŁY EKRAN całego widoku, nie jednej nieczynnej akcji.
 * Ekrany, które montują się w wielu otoczeniach (karta zgłoszenia otwiera się z ~20 miejsc, kolejka
 * bywa osadzana w landingach modułów), nie mogą się wywracać przez brakujący provider — repozytorium
 * płaciło już za to dwa razy i dlatego `KrokiDrawer` montuje się dopiero po kliknięciu, a
 * `SekcjaPoufnosciSprawy` ma własne potwierdzenie w wierszu.
 *
 * Reguła użycia: `null` = akcji wymagającej potwierdzenia NIE POKAZUJEMY W OGÓLE. Nigdy nie wolno
 * wykonać jej „bez pytania" — brak okna nie może znaczyć braku zgody.
 */
export const useConfirmOpcjonalny = () => useContext(ConfirmContext);
